import * as React from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar, Doughnut, Pie } from "react-chartjs-2";
import { Vehicle, cars as carsRaw, vehicles as vehiclesRaw } from "./Vehicle";
import ReactSelect from "react-select";
import { Input, Label } from "@Tmp/Fields";
import {
    Range,
    RangeByAmount,
    CargoWeight,
    VehicleComparisonItemProps,
    VehicleComparisonProps,
} from "./VehicleComparison.types";
import { hexToRgb, moneyFormat, extend } from "@TmpApp/Utils";
import { InputAttributes, NumberFormatBaseProps, NumericFormat } from "react-number-format";
import clsx from "clsx";
import useLocalStorage from "@Tmp/Hooks/useLocalStorage";
import { useIsClient } from "@Tmp/Hooks";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

interface CustomProps {
    onChange: (event: { target: { name: string; value: string } }) => void;
    name: string;
}

const NumberFormat = React.forwardRef<NumberFormatBaseProps<InputAttributes>, CustomProps>(
    function NumberFormatCustom(props, ref) {
        const { onChange, ...other } = props;

        return (
            <NumericFormat
                thousandSeparator=","
                decimalSeparator="."
                {...other}
                getInputRef={ref}
                onValueChange={(values) => {
                    onChange({
                        target: {
                            name: props.name,
                            value: values.value,
                        },
                    });
                }}
            />
        );
    }
);

function VehicleComparison(props: VehicleComparisonProps) {
    const isClient = useIsClient();
    const [moreOptionsOpen, setMoreOptionsOpen] = React.useState(false);
    const [powerPrice, setPowerPrice] = useLocalStorage<number>(
        "tmp-vehicle-comparison-power-price",
        props.settings?.vehicle_comparison_field_power_price_default
    );
    const [petrolPrice, setPetrolPrice] = useLocalStorage<number>(
        "tmp-vehicle-comparison-petrol-price",
        props.settings?.vehicle_comparison_field_petrol_price_default
    );

    const cars: Vehicle[] = carsRaw.map(
        (v) =>
            new Vehicle({
                ...v,
                energyPrice: v.drivingForce === "energy" ? powerPrice : petrolPrice,
            })
    );
    const vehicles: Vehicle[] = vehiclesRaw.map(
        (v) =>
            new Vehicle({
                ...v,
                energyPrice: v.drivingForce === "energy" ? powerPrice : petrolPrice,
            })
    );

    const [selectedVehicleId, setSelectedVehicleId] = useLocalStorage<number>(
        "tmp-vehicle-comparison-selected-vehicle-id",
        undefined
    );
    const [compareSelectedVehicleId, setCompareSelectedVehicleId] = useLocalStorage<number>(
        "tmp-vehicle-comparison-compare-selected-vehicle-id",
        cars?.[0]?.id
    );
    const [rangeByAmount, setRangeByAmount] = useLocalStorage<RangeByAmount>(
        "tmp-vehicle-comparison-range-by-amount",
        props.settings?.vehicle_comparison_field_range_by_amount_default
    );
    const selectedVehicle = vehicles.find((v) => v.id === selectedVehicleId);
    const compareSelectedVehicle = cars.find((v) => v.id === compareSelectedVehicleId);

    const [range, setRange] = useLocalStorage<Range>(
        "tmp-vehicle-comparison-range",
        props.settings?.vehicle_comparison_field_range_default
    );
    const [cargoWeight, setCargoWeight] = useLocalStorage<CargoWeight>(
        "tmp-vehicle-comparison-cargo-weight",
        props.settings?.vehicle_comparison_field_cargo_weight_default
    );

    const replaceHelpers = (string: string) => {
        if (!string) {
            return undefined;
        }

        if (compareSelectedVehicle && selectedVehicle) {
            string = string.replace(
                "[[savings]]",
                moneyFormat(
                    compareSelectedVehicle.costForRange(range) - selectedVehicle.costForRange(range)
                )
            );
        }

        if (selectedVehicle) {
            string = string.replace("[[vehicle]]", selectedVehicle.name());
            string = string.replace("[[vehicleWeight]]", moneyFormat(selectedVehicle.weight));
        }

        if (compareSelectedVehicle) {
            string = string.replace("[[car]]", compareSelectedVehicle.name());
            string = string.replace("[[carWeight]]", moneyFormat(compareSelectedVehicle.weight));
        }

        return string
            .replace("[[amount]]", moneyFormat(rangeByAmount))
            .replace("[[powerPrice]]", moneyFormat(powerPrice))
            .replace("[[petrolPrice]]", moneyFormat(petrolPrice))
            .replace("[[cargoWeight]]", moneyFormat(cargoWeight));
    };

    return (
        <div className="tmp-vehicle-comparison">
            <div className="tmp-vehicle-comparison__toolbar">
                <Input
                    wrapper={{ className: "tmp-vehicle-comparison__range-input-container" }}
                    label={props.settings?.vehicle_comparison_field_range_label}
                    value={range}
                    onChange={(e) => setRange(parseInt(e.target.value))}
                    inputComponent={NumberFormat}
                    input={{ suffix: "km." } as any}
                />
                <div className="tmp-vehicle-comparison__select">
                    <Label label={props.settings?.vehicle_comparison_field_vehicle_label} />
                    {isClient && (
                        <ReactSelect
                            value={selectedVehicle}
                            onChange={(newValue: any, meta) => {
                                setSelectedVehicleId(newValue?.id);
                            }}
                            options={vehicles}
                            getOptionLabel={(option: any) => option.brand + " " + option.model}
                            getOptionValue={(option: any) => option.model}
                            isClearable
                            placeholder={
                                props.settings?.vehicle_comparison_field_vehicle_placeholder
                            }
                            styles={{
                                container: (provided, state) => ({
                                    ...provided,
                                    width: "100%",
                                }),
                            }}
                        />
                    )}
                </div>
                <div className="tmp-vehicle-comparison__select">
                    <Label label={props.settings?.vehicle_comparison_field_cars_label} />
                    {isClient && (
                        <ReactSelect
                            value={compareSelectedVehicle}
                            onChange={(newValue: any, meta) => {
                                setCompareSelectedVehicleId(newValue?.id);
                            }}
                            options={cars}
                            getOptionLabel={(option: any) => option.brand + " " + option.model}
                            getOptionValue={(option: any) => option.model}
                            isClearable
                            placeholder={props.settings?.vehicle_comparison_field_cars_placeholder}
                            styles={{
                                container: (provided, state) => ({
                                    ...provided,
                                    width: "100%",
                                }),
                            }}
                        />
                    )}
                </div>
                <div
                    className="tmp-vehicle-comparison__toolbar__settings"
                    onClick={() => setMoreOptionsOpen((prev) => !prev)}
                >
                    <span className="tmp-label-text">
                        {moreOptionsOpen
                            ? props.settings?.vehicle_comparison_field_settings_show_less_label ??
                              "Skjul indstillinger"
                            : props.settings?.vehicle_comparison_field_settings_show_more_label ??
                              "Flere indstillinger"}
                    </span>
                </div>
                {moreOptionsOpen && (
                    <>
                        <Input
                            label={props.settings?.vehicle_comparison_field_range_by_amount_label}
                            value={rangeByAmount}
                            onChange={(e) => setRangeByAmount(parseInt(e.target.value))}
                            inputComponent={NumberFormat}
                            input={{ suffix: "kr." } as any}
                        />

                        <Input
                            label={props.settings?.vehicle_comparison_field_cargo_weight_label}
                            value={cargoWeight}
                            onChange={(e) => setCargoWeight(parseInt(e.target.value))}
                            inputComponent={NumberFormat}
                            input={{ suffix: "kg." } as any}
                        />
                        <div className="tmp-vehicle-comparison__toolbar__energy-prices-container">
                            <Input
                                label={props.settings?.vehicle_comparison_field_power_price_label}
                                value={powerPrice}
                                onChange={(e) => setPowerPrice(parseInt(e.target.value))}
                                inputComponent={NumberFormat}
                                input={{ suffix: "kr." } as any}
                            />
                            <Input
                                label={props.settings?.vehicle_comparison_field_petrol_price_label}
                                value={petrolPrice}
                                onChange={(e) => setPetrolPrice(parseInt(e.target.value))}
                                inputComponent={NumberFormat}
                                input={{ suffix: "kr." } as any}
                            />
                        </div>
                    </>
                )}
            </div>

            {isClient && selectedVehicle && compareSelectedVehicle && (
                <>
                    <h2 className="tmp-vehicle-comparison__heading">
                        {replaceHelpers(props.settings?.vehicle_comparison_heading)}
                    </h2>
                    <div className="tmp-vehicle-comparison__list">
                        {props.settings.vehicle_comparison_datasets?.map((item) => (
                            <VehicleComparisonItem
                                key={item.vehicle_comparison_datasets_dataset}
                                item={item}
                                settings={props.settings}
                                vehicles={[compareSelectedVehicle, selectedVehicle]}
                                rangeByAmount={rangeByAmount}
                                range={range}
                                cargoWeight={cargoWeight}
                                replaceHelpers={replaceHelpers}
                            />
                        ))}
                    </div>
                    <div
                        dangerouslySetInnerHTML={{
                            __html: replaceHelpers(props.settings?.vehicle_comparison_footer_text),
                        }}
                    />
                </>
            )}
        </div>
    );
}

/*function VehicleComparisonToolbarDropdown(props) {
    return (
        <>
            <Input
                label={props.settings?.vehicle_comparison_field_range_by_amount_label}
                value={rangeByAmount}
                onChange={(e) => setRangeByAmount(parseInt(e.target.value))}
                inputComponent={NumberFormat}
                input={{ suffix: "kr." } as any}
            />

            <Input
                label={props.settings?.vehicle_comparison_field_cargo_weight_label}
                value={cargoWeight}
                onChange={(e) => setCargoWeight(parseInt(e.target.value))}
                inputComponent={NumberFormat}
                input={{ suffix: "kg." } as any}
            />
            <div className="tmp-vehicle-comparison__toolbar__energy-prices-container">
                <Input
                    label={props.settings?.vehicle_comparison_field_power_price_label}
                    value={powerPrice}
                    onChange={(e) => setPowerPrice(parseInt(e.target.value))}
                    inputComponent={NumberFormat}
                    input={{ suffix: "kr." } as any}
                />
                <Input
                    label={props.settings?.vehicle_comparison_field_petrol_price_label}
                    value={petrolPrice}
                    onChange={(e) => setPetrolPrice(parseInt(e.target.value))}
                    inputComponent={NumberFormat}
                    input={{ suffix: "kr." } as any}
                />
            </div>
        </>
    );
}*/

function VehicleComparisonItem(props: VehicleComparisonItemProps) {
    let Component = VehicleComparisonBar;
    switch (props.item?.vehicle_comparison_datasets_dataset) {
        case "efficiency":
            Component = VehicleComparisonPie;
            break;
    }

    return (
        <div
            className={clsx("tmp-vehicle-comparison__list__item", {
                "tmp-vehicle-comparison__list__item__circles-container":
                    props.item?.vehicle_comparison_datasets_dataset === "efficiency",
            })}
        >
            <Component {...props} />
        </div>
    );
}

function VehicleComparisonBar({
    item,
    rangeByAmount,
    range,
    vehicles,
    replaceHelpers,
    ...props
}: VehicleComparisonItemProps) {
    let dataset = undefined;

    const vehicleRgb = hexToRgb(props.settings?.vehicle_comparison_charts_vehicle_color);
    const carRgb = hexToRgb(props.settings?.vehicle_comparison_charts_car_color);

    const datasetDefaults = {
        label: replaceHelpers(item?.vehicle_comparison_datasets_title),
        borderColor: [
            "rgba(" + carRgb.r + "," + carRgb.g + "," + carRgb.b + ",1)",
            "rgba(" + vehicleRgb.r + "," + vehicleRgb.g + "," + vehicleRgb.b + ",1)",
        ],
        backgroundColor: [
            "rgba(" + carRgb.r + "," + carRgb.g + "," + carRgb.b + ",1)",
            "rgba(" + vehicleRgb.r + "," + vehicleRgb.g + "," + vehicleRgb.b + ",1)",
        ],
    };

    let options = {};

    switch (item?.vehicle_comparison_datasets_dataset) {
        case "km_price":
            dataset = {
                data: vehicles.map((v) => v.kmPrice()),
                ...datasetDefaults,
            };

            options = {
                scales: {
                    xAxes: {
                        ticks: {
                            callback: function (value, index, ticks) {
                                return value + "kr.";
                            },
                        },
                    },
                },
                plugins: {
                    datalabels: {
                        formatter: (value, ctx) => {
                            return moneyFormat(value) + "kr.";
                        },
                    },
                },
            };
            break;
        case "range_by_amount":
            dataset = {
                data: vehicles.map((v) => v.rangeByAmount(rangeByAmount)),
                ...datasetDefaults,
            };

            options = {
                scales: {
                    xAxes: {
                        ticks: {
                            callback: function (value, index, ticks) {
                                return value + "km.";
                            },
                        },
                    },
                },
                plugins: {
                    datalabels: {
                        formatter: (value, ctx) => {
                            return moneyFormat(value) + "km.";
                        },
                    },
                },
            };
            break;
        case "refill_price":
            dataset = {
                data: vehicles.map((v) => v.fullRefillPrice()),
                ...datasetDefaults,
            };

            options = {
                scales: {
                    xAxes: {
                        ticks: {
                            callback: function (value, index, ticks) {
                                return value + "kr.";
                            },
                        },
                    },
                },
                plugins: {
                    datalabels: {
                        formatter: (value, ctx) => {
                            return moneyFormat(value) + "kr.";
                        },
                    },
                },
            };
            break;
        case "range":
            dataset = {
                data: vehicles.map((v) => v.range),
                ...datasetDefaults,
            };

            options = {
                scales: {
                    xAxes: {
                        ticks: {
                            callback: function (value, index, ticks) {
                                return value + "km.";
                            },
                        },
                    },
                },
                plugins: {
                    datalabels: {
                        formatter: (value, ctx) => {
                            return moneyFormat(value) + "km.";
                        },
                    },
                },
            };
            break;
    }

    const labels = [];
    vehicles.map((v) => {
        const a = v.name().match(/.{10}\w*\W*|.*/g);

        const name = [];
        a.forEach(function (entry) {
            name.push(entry.trim());
        });

        labels.push(name);
    });

    const data = {
        labels: labels,
        datasets: [dataset],
    };

    if (!dataset) {
        return null;
    }

    return (
        <>
            <div className="tmp-vehicle-comparison__list__item__canvas-container">
                <Bar
                    data={data}
                    options={extend(
                        true,
                        {
                            indexAxis: "y" as const,
                            elements: {
                                bar: {
                                    borderWidth: 2,
                                },
                            },
                            scales: {
                                xAxes: {
                                    ticks: {
                                        callback: function (value, index, ticks) {
                                            return value + "km.";
                                        },
                                    },
                                },
                            },
                            maintainAspectRatio: false,
                            layout: {
                                padding: {
                                    right: 80,
                                },
                            },
                            plugins: {
                                title: {
                                    display: true,
                                    text: replaceHelpers(item?.vehicle_comparison_datasets_title),
                                    font: { size: 18 },
                                },
                                legend: {
                                    display: false,
                                },
                                tooltip: {
                                    enabled: false,
                                },
                                datalabels: {
                                    formatter: (value, ctx) => {
                                        return moneyFormat(value);
                                    },
                                    anchor: "end",
                                    align: "end",
                                },
                            },
                        },
                        options
                    )}
                />
            </div>
            <div
                className="tmp-vehicle-comparison__list__item__description"
                dangerouslySetInnerHTML={{
                    __html: replaceHelpers(item?.vehicle_comparison_datasets_description),
                }}
            />
        </>
    );
}

function VehicleComparisonPie({
    item,
    rangeByAmount,
    cargoWeight,
    vehicles,
    replaceHelpers,
    ...props
}: VehicleComparisonItemProps) {
    const weightRgb = hexToRgb(props.settings?.vehicle_comparison_charts_vehicle_weight_color);
    const cargoRgb = hexToRgb(props.settings?.vehicle_comparison_charts_vehicle_cargo_color);

    return (
        <>
            <h3 className="tmp-vehicle-comparison__list__item__circles-heading">
                {replaceHelpers(item?.vehicle_comparison_datasets_title)}
            </h3>
            <div className="tmp-vehicle-comparison__list__item__circles">
                {vehicles.map((vehicle) => {
                    const data = {
                        labels: [
                            replaceHelpers(
                                "Energiforbrug i % for at flytte [[vehicle]]".replace(
                                    "[[vehicle]]",
                                    vehicle.name()
                                )
                            ),
                            "Energiforbrug i % for at transportere dig",
                        ],
                        datasets: [
                            {
                                data: [
                                    vehicle.percentageUsageVehicle(cargoWeight),
                                    vehicle.percentageUsageCargo(cargoWeight),
                                ],
                                backgroundColor: [
                                    "rgba(" +
                                        weightRgb.r +
                                        "," +
                                        weightRgb.g +
                                        "," +
                                        weightRgb.b +
                                        ",1)",
                                    "rgba(" +
                                        cargoRgb.r +
                                        "," +
                                        cargoRgb.g +
                                        "," +
                                        cargoRgb.b +
                                        ",1)",
                                ],
                                borderColor: [
                                    "rgba(" +
                                        weightRgb.r +
                                        "," +
                                        weightRgb.g +
                                        "," +
                                        weightRgb.b +
                                        ",1)",
                                    "rgba(" +
                                        cargoRgb.r +
                                        "," +
                                        cargoRgb.g +
                                        "," +
                                        cargoRgb.b +
                                        ",1)",
                                ],
                                borderWidth: 1,
                            },
                        ],
                    };

                    return (
                        <div
                            key={vehicle.model}
                            className="tmp-vehicle-comparison__list__item__circle-container"
                        >
                            <h4 className="tmp-vehicle-comparison__list__item__circle-heading">
                                {vehicle.name()}
                            </h4>
                            <ul className="tmp-vehicle-comparison__list__item__circle-legends">
                                <li className="tmp-vehicle-comparison__list__item__circle-legend">
                                    <div
                                        className="tmp-vehicle-comparison__list__item__circle-legend__box"
                                        style={{
                                            backgroundColor:
                                                "rgba(" +
                                                weightRgb.r +
                                                "," +
                                                weightRgb.g +
                                                "," +
                                                weightRgb.b +
                                                ",1)",
                                        }}
                                    />
                                    <span className="tmp-vehicle-comparison__list__item__circle-legend__label">
                                        {replaceHelpers(
                                            "Energiforbrug i % for at flytte [[vehicle]]".replace(
                                                "[[vehicle]]",
                                                vehicle.name()
                                            )
                                        )}
                                    </span>
                                </li>
                                <li className="tmp-vehicle-comparison__list__item__circle-legend">
                                    <div
                                        className="tmp-vehicle-comparison__list__item__circle-legend__box"
                                        style={{
                                            backgroundColor:
                                                "rgba(" +
                                                cargoRgb.r +
                                                "," +
                                                cargoRgb.g +
                                                "," +
                                                cargoRgb.b +
                                                ",1)",
                                        }}
                                    />
                                    <span className="tmp-vehicle-comparison__list__item__circle-legend__label">
                                        {"Energiforbrug i % for at transportere dig"}
                                    </span>
                                </li>
                            </ul>
                            <div className="tmp-vehicle-comparison__list__item__canvas-container">
                                <Doughnut
                                    data={data}
                                    options={{
                                        cutout: 70,
                                        maintainAspectRatio: false,

                                        plugins: {
                                            title: {
                                                display: false,
                                            },
                                            datalabels: {
                                                formatter: (value, ctx) => {
                                                    let sum = 0;
                                                    let dataArr = ctx.chart.data.datasets[0].data;
                                                    dataArr.map((data) => {
                                                        if (typeof data === "number") {
                                                            sum += data;
                                                        }
                                                    });
                                                    let percentage =
                                                        moneyFormat((value * 100) / sum) + "%";
                                                    return percentage;
                                                },
                                                color: "#fff",
                                            },
                                            legend: {
                                                display: false,
                                                onClick: () => {},
                                            },
                                            tooltip: {
                                                enabled: false,
                                            },
                                        },
                                    }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
            <div
                className="tmp-vehicle-comparison__list__item__description"
                dangerouslySetInnerHTML={{
                    __html: replaceHelpers(item?.vehicle_comparison_datasets_description),
                }}
            />
        </>
    );
}

export default VehicleComparison;
